// Public marketing content shared by the landing page, /pricing, and /help.
// Plan numbers mirror the SubscriptionProducts document in Firestore
// (backend scripts/seed-data/_subscriptionProducts.json). Keep them in sync
// when prices or limits change, along with the Product JSON-LD in public/index.html.

export const PRIVACY_LINKS = {
  noSale: '/privacy.html#how-we-share',
  noTraining: '/privacy.html#ai-assistant',
  cardData: '/privacy.html#information-we-collect',
};

export const TERMS_CANCELLATION_URL = '/tou.html#cancellation';

export const AI_TOKEN_ALLOWANCE = {
  individual: 100000,
  individualMax: 200000,
};

/**
 * Shown when /api/subscriptionProducts can't be reached, so visitors always
 * see real prices. stripePriceId is null: the CTA opens sign-up and the plan
 * is picked on the subscription page after sign-in.
 */
export const FALLBACK_PLANS = [
  {
    id: 'individual',
    stripePriceId: null,
    name: 'Individual',
    price: '$11',
    period: 'month',
    description: 'Everything a solo reseller needs: inventory, expenses, mileage, analytics.',
    features: [
      'Up to 750 active items per sales channel',
      'Up to 3 images per item',
      'Up to 10 sales channels',
      'Reports & Analytics',
      'Email support',
      `Up to ${AI_TOKEN_ALLOWANCE.individual.toLocaleString('en-US')} AI assistant tokens per month`,
      'Connect 1 sales channel to Square, Shopify, or another platform',
    ],
    cta: 'Start Free Trial',
    highlighted: true,
    trialPeriodDays: 7,
  },
  {
    id: 'individual-max',
    stripePriceId: null,
    name: 'Individual Max',
    price: '$20',
    period: 'month',
    description: 'Higher limits plus a larger AI assistant allowance.',
    features: [
      'Up to 1500 active items per sales channel',
      'Up to 3 images per item',
      'Up to 20 sales channels',
      'Reports & Analytics',
      'Email support',
      `Up to ${AI_TOKEN_ALLOWANCE.individualMax.toLocaleString('en-US')} AI assistant tokens per month`,
      'Connect up to 3 sales channels to Square, Shopify, or another platform',
    ],
    cta: 'Start Free Trial',
    highlighted: false,
    trialPeriodDays: 7,
  },
];

/** Questions a visitor asks before signing up. Rendered on /help and as FAQPage JSON-LD. */
export const PRE_SIGNUP_FAQ = [
  {
    question: 'Does the free trial need a card?',
    answer:
      "Yes. A payment method is required to start the 7-day free trial, but you won't be charged until day 8. Cancel any time before then and you pay nothing. Payments run through Stripe, so Reseller Pro never sees or stores your card details.",
  },
  {
    question: 'How do I import my existing inventory?',
    answer:
      "Open Import Data from the sidebar, choose Inventory, and upload a CSV or Excel file (.xlsx, .xls, .xlsm). Reseller Pro matches your spreadsheet's column headers to its fields automatically, lets you adjust the mapping and edit rows, then saves everything in one step. Expenses, sales transactions, and mileage import the same way.",
  },
  {
    question: 'What happens to my data if I cancel?',
    answer:
      'Your data is kept for up to 90 days after you pause or cancel, so you can restart your subscription and pick up where you left off. You can export your inventory, expenses, sales, and mileage to CSV at any time from Export Data. If you want everything permanently deleted, contact support.',
  },
  {
    question: "What are the AI assistant's limits?",
    answer: `The assistant is included in every plan with a monthly token allowance: ${AI_TOKEN_ALLOWANCE.individual.toLocaleString('en-US')} tokens on Individual and ${AI_TOKEN_ALLOWANCE.individualMax.toLocaleString('en-US')} on Individual Max. Longer questions and answers use more tokens. You can see your usage in the app, and the allowance resets each month. Your data is never used to train AI models.`,
  },
  {
    question: 'How do I cancel?',
    answer:
      "Go to Account Management in the sidebar and open the billing portal, where you can cancel, pause, or restart your subscription. If you're on the free trial, cancel before it ends and you won't be charged.",
  },
];
